import * as pc from "playcanvas";

export function roundToDecimals(value: number, decimals: number): number {
    const factor = 10 ** decimals;
    return Math.round(value * factor) / factor;
}

export function vec3Distance(a: pc.Vec3, b: pc.Vec3): number {
    const temp = new pc.Vec3().sub2(a, b);
    return temp.length();
}

export function hexToRgb(hex: string) {
    const r = parseInt(hex.slice(1, 3), 16) / 255;
    const g = parseInt(hex.slice(3, 5), 16) / 255;
    const b = parseInt(hex.slice(5, 7), 16) / 255;
    return { r, g, b }; // JSON object format
}

export function hexToRgbArray(hex: string) {
    const r = parseInt(hex.slice(1, 3), 16) / 255;
    const g = parseInt(hex.slice(3, 5), 16) / 255;
    const b = parseInt(hex.slice(5, 7), 16) / 255;
    return [r, g, b];
}

export function wrapAngle(angle: number) {
    while (angle > 180) angle -= 360;
    while (angle < -180) angle += 360;
    return angle;
}

export function getYAngleDegree(pos1: pc.Vec3, pos2: pc.Vec3) {
    const direction = new pc.Vec3();
    direction.sub2(pos2, pos1);
    direction.y = 0;
    const angle = Math.atan2(direction.x, direction.z);
    const angleDegrees = pc.math.RAD_TO_DEG * angle;
    return -angleDegrees;
}

export function interpolateVector3(points: number[][], percentage: number) {
    if (percentage <= 0) return points[0];
    if (percentage >= 1) return points[points.length - 1];
    const segments = points.length - 1;
    const scaledPosition = percentage * segments;
    const segmentIndex = Math.floor(scaledPosition);
    const segmentFraction = scaledPosition - segmentIndex;
    const start = points[segmentIndex];
    const end = points[segmentIndex + 1];
    const result = [
        start[0] + (end[0] - start[0]) * segmentFraction,
        start[1] + (end[1] - start[1]) * segmentFraction,
        start[2] + (end[2] - start[2]) * segmentFraction,
    ];
    return result;
}

export function getBackPosition(origin: pc.Vec3, front: pc.Vec3) {
    const backPosition = new pc.Vec3(
        2 * origin.x - front.x,
        2 * origin.y - front.y,
        2 * origin.z - front.z,
    );
    return backPosition;
}

export function getNewFrontPosition(origin: pc.Vec3, front: pc.Vec3, distance: number) {
    const vx = front.x - origin.x;
    const vy = front.y - origin.y;
    const vz = front.z - origin.z;
    const L = Math.sqrt(vx * vx + vy * vy + vz * vz);
    if (L === 0) {
        return { ...origin };
    }
    const scale = distance / L;
    const frontPosition = new pc.Vec3(
        origin.x + vx * scale,
        origin.y + vy * scale,
        origin.z + vz * scale,
    );
    return frontPosition;
}

export function getBackPositionWithDistance(
    origin: pc.Vec3,
    front: pc.Vec3,
    distance: number,
) {
    const vx = front.x - origin.x;
    const vy = front.y - origin.y;
    const vz = front.z - origin.z;
    const L = Math.sqrt(vx * vx + vy * vy + vz * vz);
    if (L === 0) {
        // front == origin, direction undefined
        return { ...origin };
    }

    const scale = distance / L;
    const backPosition = new pc.Vec3(
        origin.x - vx * scale,
        origin.y - vy * scale,
        origin.z - vz * scale,
    );
    return backPosition;
}

export function angleDifferenceABtoAC(
    A: pc.Vec3,
    B: pc.Vec3,
    C: pc.Vec3,
): number {
    // Direction vectors: AB = B - A, AC = C - A
    const AB = new pc.Vec3().sub2(B, A);
    const AC = new pc.Vec3().sub2(C, A);

    // Clamp lengths to avoid div-by-zero
    const lenAB = AB.length();
    const lenAC = AC.length();
    if (lenAB < 1e-6 || lenAC < 1e-6) {
        return 0;
    }

    // FIXED: Use instance method AB.dot(AC), not static pc.Vec3.dot()
    const cosTheta = AB.dot(AC) / (lenAB * lenAC);

    // Clamp cosine for numerical stability
    const clampedCos = Math.max(-1, Math.min(1, cosTheta));

    return Math.acos(clampedCos); // radians (0 to π)
}

export function calculateLookAtRotation(
    posA: pc.Vec3,
    posB: pc.Vec3,
    up = pc.Vec3.UP,
) {
    const dir = new pc.Vec3();
    dir.sub2(posB, posA).normalize();
    const quat = new pc.Quat();
    quat.setFromMat4(new pc.Mat4().setLookAt(posA, posB, up));
    return quat;
}

export function calculateTargetPosition(
    px: number,
    py: number,
    pz: number,
    qx: number,
    qy: number,
    qz: number,
    qw: number,
): pc.Vec3 {
    // Create position and quaternion from parameters
    const position = new pc.Vec3(px, py, pz);
    const quaternion = new pc.Quat(qx, qy, qz, qw);

    // Forward vector in PlayCanvas (-Z direction)
    const forward = new pc.Vec3(0, 0, -1);

    // Transform forward vector by quaternion (instance method, not static)
    const direction = quaternion.transformVector(forward).normalize();

    // Target = position + 1 unit * direction
    return position.clone().add(direction);
}

export function normalizeRotation(vec3: pc.Vec3) {
    const normalized = new pc.Vec3();
    // Wrap to [0, 360)
    normalized.x = ((vec3.x % 360) + 360) % 360;
    normalized.y = ((vec3.y % 360) + 360) % 360;
    normalized.z = ((vec3.z % 360) + 360) % 360;
    // Optional: Remap to [-180, 360) for gimbal-friendly range
    if (normalized.x > 180) normalized.x -= 360;
    if (normalized.y > 180) normalized.y -= 360;
    if (normalized.z > 180) normalized.z -= 360;
    return normalized;
}

export function tri(x: number) {
    // return 1 - Math.abs(2 * x - 1);
    // Buffer zone: 0 to 0.1 stays 0
    if (x <= 0.1) return 0;

    // Normalize remaining range [0.1, 1.0] -> [0, 1]
    const normalized = (x - 0.1) / 0.9;

    // Apply triangle function
    return 1 - Math.abs(2 * normalized - 1);
}

export const radians = (deg: number) => {
    return (deg * Math.PI) / 180;
};

export const getDeviceType = () => {
    if (typeof navigator === "undefined" || typeof window === "undefined") {
        return "ssr"; // Server-side rendering
    }

    const ua = navigator.userAgent;
    const isMobile =
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
            ua,
        );

    // Modern iPad detection (iPadOS pretends to be Mac)
    const isIOS =
        /iPad|iPhone|iPod/.test(ua) ||
        (navigator.platform === "MacIntel" &&
            navigator.maxTouchPoints &&
            navigator.maxTouchPoints > 1);

    const isAndroid = /android/i.test(ua);

    if (isIOS) return "ios";
    if (isAndroid) return "android";
    if (isMobile) return "mobile";
    return "desktop";
};

export async function checkCameraPermission() {
    if (!navigator.permissions || !navigator.permissions.query)
        return "unknown";
    const status = await navigator.permissions.query({ name: "camera" });
    return status.state; // 'granted', 'denied', or 'prompt'
}

export async function requestCameraPermission() {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia)
        return "denied";
    try {
        await navigator.mediaDevices.getUserMedia({ video: true });
        return "granted";
    } catch {
        return "denied";
    }
}

// ✅ Type-safe, works on all browsers including iOS Safari 13+
export async function checkGyroscopePermission(): Promise<PermissionState> {
    // Fallback for browsers without Permissions API
    if (!navigator.permissions?.query) return "prompt";

    try {
        // Type assertion needed - 'gyroscope' not in PermissionName
        const status = await navigator.permissions.query({
            name: "gyroscope" as PermissionName,
        });
        return status.state; // 'granted' | 'denied' | 'prompt'
    } catch {
        return "prompt"; // Unsupported browser
    }
}

export function interpolateVector3Array(points: any, percentage: number) {
    if (percentage <= 0) return points[0];
    if (percentage >= 1) return points[points.length - 1];

    const segments = points.length - 1;
    const scaledPosition = percentage * segments;
    const segmentIndex = Math.floor(scaledPosition);
    const segmentFraction = scaledPosition - segmentIndex;

    const start = points[segmentIndex];
    const end = points[segmentIndex + 1];

    return [
        start[0] + (end[0] - start[0]) * segmentFraction,
        start[1] + (end[1] - start[1]) * segmentFraction,
        start[2] + (end[2] - start[2]) * segmentFraction,
    ];
}

export async function requestGyroscopePermission(): Promise<PermissionState> {
    // Check for iOS-specific requestPermission (not in TypeScript DOM types)
    const hasIOSPermissionAPI =
        typeof (DeviceOrientationEvent as any)?.requestPermission ===
        "function";

    if (hasIOSPermissionAPI) {
        try {
            const permissionState = await (
                DeviceOrientationEvent as any
            ).requestPermission();
            return permissionState === "granted" ? "granted" : "denied";
        } catch (error) {
            console.warn("iOS gyro permission request failed:", error);
            return "denied";
        }
    }

    // Non-iOS browsers - try Permissions API
    if (navigator.permissions?.query) {
        try {
            const status = await navigator.permissions.query({
                name: "gyroscope" as PermissionName,
            });
            if (status.state === "prompt") {
                // Trigger via deviceorientation event (common pattern)
                return new Promise((resolve) => {
                    const handleOrientation = () => {
                        window.removeEventListener(
                            "deviceorientation",
                            handleOrientation,
                        );
                        resolve("granted");
                    };
                    window.addEventListener(
                        "deviceorientation",
                        handleOrientation,
                        { once: true },
                    );

                    // Trigger event to prompt permission
                    if (window.DeviceOrientationEvent) {
                        window.dispatchEvent(
                            new window.DeviceOrientationEvent(
                                "deviceorientation",
                            ),
                        );
                    } else {
                        resolve("denied");
                    }
                });
            }
            return status.state;
        } catch {
            return "prompt";
        }
    }

    return "denied"; // No API support
}
